import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';
import logoImg from '../../Common/Images/KE_Logo.png';
import submit_n from '../../Common/Images/submit_n.gif';
import submit_o from '../../Common/Images/submit_o.gif';
import legendImg from '../../Common/Images/legend.jpg';

import ATestImg from '../Images/ATest.JPG';
import BrickImg from '../Images/Brick_6.png';
import KnowledgeImg from '../Images/KnowledgeTest.JPG';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
const ITEMS_PER_PAGE = 50;

interface ReportEntry {
    typeId: number;
    activity: string;
    lastDoneOn: string;
    lastScore: number;
    averageScore: number;
    timesDone: number;
    chapter: string;
    unit: string;
    level: string;
    color: string;
}

interface DropdownOption {
    value: string;
    label: string;
}

type SortKey = keyof ReportEntry;
type SortDirection = 'asc' | 'desc';
type SortType = 'date' | 'number' | 'text';

interface SortConfig {
    key: SortKey;
    direction: SortDirection;
}

interface ColumnDef {
    key: SortKey;
    label: string;
    type: SortType;
    center?: boolean;
    width?: string;
    firstDirection?: SortDirection;
}

const COLUMNS: ColumnDef[] = [
    { key: 'typeId', label: 'Type', type: 'number', center: true, firstDirection: 'asc' },
    { key: 'activity', label: 'Activity', type: 'text', width: '1%' },
    { key: 'lastDoneOn', label: 'Last Done On', type: 'date', width: '1%' },
    { key: 'lastScore', label: 'Last Score', type: 'number', center: true, width: '1%' },
    { key: 'averageScore', label: 'Average Score', type: 'number', center: true, width: '1%' },
    { key: 'timesDone', label: 'Times Done', type: 'number', center: true, width: '1%' },
    { key: 'chapter', label: 'Chapter', type: 'text', width: '1%' },
    { key: 'unit', label: 'Unit', type: 'text', width: '1%' },
    { key: 'level', label: 'Level', type: 'text', width: '90%' },
];

// ---------- Sort helpers ----------

// Returns null when the value cannot be interpreted (those rows always go last)
const parseNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return isNaN(value) ? null : value;
    const str = String(value).trim();
    if (!str) return null;

    const num = str.match(/-?\d+(?:\.\d+)?/);
    return num ? parseFloat(num[0]) : null;
};

// Handles "dd/mm/yyyy hh:mm(:ss) [AM/PM]" (and "-" separators), falls back to Date.parse
const parseDateTime = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;

    const m = str.match(
        /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})(?:[ T,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?/
    );
    if (m) {
        const day = parseInt(m[1], 10);
        const month = parseInt(m[2], 10) - 1;
        let year = parseInt(m[3], 10);
        if (year < 100) year += 2000;
        let hour = m[4] ? parseInt(m[4], 10) : 0;
        const minute = m[5] ? parseInt(m[5], 10) : 0;
        const second = m[6] ? parseInt(m[6], 10) : 0;
        if (m[7]) {
            const pm = m[7].toLowerCase() === 'pm';
            if (pm && hour < 12) hour += 12;
            if (!pm && hour === 12) hour = 0;
        }
        return new Date(year, month, day, hour, minute, second).getTime();
    }

    const parsed = Date.parse(str);
    return isNaN(parsed) ? null : parsed;
};

const getSortValue = (row: ReportEntry, col: ColumnDef): number | string | null => {
    const raw = row[col.key];
    switch (col.type) {
        case 'date': return parseDateTime(raw);
        case 'number': return parseNumber(raw);
        default: {
            const s = (raw ?? '').toString().trim().toLowerCase();
            return s === '' ? null : s;
        }
    }
};

const styles = {
    container: {
        position: 'absolute' as 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column' as 'column',
        fontFamily: 'Verdana, Arial, Helvetica, sans-serif', fontSize: '11px',
        backgroundColor: '#FFFFFF', overflow: 'hidden'
    },
    headerSection: {
        backgroundColor: 'white',
        borderBottom: '1px solid #999',
        flexShrink: 0
    },
    topBanner: {
        background: 'linear-gradient(to right, #7196B3, #AEC8D9)',
        padding: '5px 10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white',
        height: '45px'
    },
    logo: { height: '35px', marginRight: '10px' },
    pageTitle: {
        background: '#2F6089',
        fontSize: '10pt',
        fontWeight: 'bold' as 'bold',
        color: '#FFD700',
        padding: '5px 10px',
        fontFamily: 'Trebuchet MS, Arial, sans-serif'
    },
    controlsPanel: {
        padding: '10px',
        backgroundColor: '#E9FEF5',
        borderBottom: '1px solid #A0A0A0'
    },
    controlsTable: {
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        fontSize: '11px'
    },
    controlLabel: {
        fontFamily: 'Arial',
        fontWeight: 'bold' as 'bold',
        fontSize: '10pt',
        color: '#2F6089',
        textAlign: 'right' as 'right',
        paddingRight: '5px',
        whiteSpace: 'nowrap' as 'nowrap',
        verticalAlign: 'middle' as 'middle'
    },
    controlCell: {
        textAlign: 'left' as 'left',
        paddingRight: '15px',
        verticalAlign: 'middle' as 'middle'
    },
    select: {
        border: '1px solid #7F9DB9',
        padding: '2px',
        fontSize: '11px',
        fontFamily: 'Verdana',
        width: '100%',
        maxWidth: '200px'
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#FFFFFF'
    },
    dataTable: {
        width: '100%',
        borderCollapse: 'collapse' as 'collapse',
        fontSize: '11px',
        border: '1px solid #A0A0A0'
    },
    th: {
        fontFamily: 'Arial',
        backgroundColor: '#C7DDFD',
        color: '#2859ac',
        fontSize: '10pt',
        padding: '8px',
        fontWeight: 'normal',
        border: '1px solid #a3c0e8',
        textAlign: 'left' as 'left',
        position: 'sticky' as 'sticky',
        top: 0
    },
    thSortable: {
        cursor: 'pointer',
        userSelect: 'none' as const
    },
    sortArrow: {
        display: 'inline-block',
        marginLeft: '4px',
        fontSize: '8pt',
        minWidth: '8px'
    },
    td: {
        padding: '8px',
        fontFamily: 'Arial',
        fontSize: '10pt',
        borderRight: '1px solid #a3c0e8',
        color: 'black'
    },
    errorBox: {
        color: 'red',
        padding: '10px',
        border: '1px solid red',
        marginBottom: '10px',
        backgroundColor: '#ffe6e6',
        fontSize: '11px'
    },
    paginationBar: {
        fontFamily: 'Arial',
        padding: '8px',
        textAlign: 'center' as 'center',
        backgroundColor: '#C7DDFD',
        borderTop: '1px solid #A0A0A0',
        fontSize: '12pt',
        color: 'black'
    },
    pageLink: {
        cursor: 'pointer',
        margin: '0 5px',
        textDecoration: 'underline',
        color: '#0000FF'
    },
    pageLinkDisabled: {
        margin: '0 5px',
        color: '#999',
        textDecoration: 'none',
        cursor: 'default'
    }
};

const EL_PTReport: React.FC = () => {
    const [searchParams] = useSearchParams();
    const schoolCode = searchParams.get('SchoolCode') || '';
    const familyCode = searchParams.get('FamilyCode') || '';
    const loginParam = searchParams.get('Login') || '';
    const cnameParam = searchParams.get('cname') || '';
    const calledFrom = searchParams.get('CalledFrom') || '';

    const [hasSearched, setHasSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingSubjects, setLoadingSubjects] = useState(false);
    const [loadingLevels, setLoadingLevels] = useState(false);

    // Filter State
    const [students, setStudents] = useState<DropdownOption[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<string>('');
    const [subjects, setSubjects] = useState<DropdownOption[]>([]);
    const [selectedSubject, setSelectedSubject] = useState<string>('');
    const [levels, setLevels] = useState<DropdownOption[]>([]);
    const [selectedLevel, setSelectedLevel] = useState<string>('');
    const [performanceOptions] = useState<DropdownOption[]>([
        { value: 'ALL', label: 'ALL' },
        { value: '2', label: 'Requires Attention' },
        { value: '3', label: 'Good' },
        { value: '4', label: 'Excellent' }
    ]);
    const [selectedPerformance, setSelectedPerformance] = useState<string>('ALL');

    const [reportData, setReportData] = useState<ReportEntry[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isHovered, setIsHovered] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

    const navigate = useNavigate();

    // 1. Fetch Students
    // 1. Fetch Students
    useEffect(() => {
        const fetchStudents = async () => {
            setLoading(true);

            try {
                const response = await fetch(
                    `${API_BASE_URL}api/diary/students/?` +
                    `SchoolCode=${encodeURIComponent(schoolCode)}` +
                    `&FamilyCode=${encodeURIComponent(familyCode)}` +
                    `&Login=${encodeURIComponent(loginParam)}` +
                    `&CalledFrom=${encodeURIComponent(calledFrom)}`
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch students');
                }

                const data = await response.json();

                // Keep ALL students returned by the API
                const studentOptions: DropdownOption[] = data.map((s: any) => ({
                    value: s.Login || s.id,
                    label: s.FirstName || s.first_name || '',
                }));

                // Add Select option only when there are multiple students
                if (studentOptions.length > 1) {
                    studentOptions.unshift({
                        value: '',
                        label: '-- Select --'
                    });
                }

                setStudents(studentOptions);

                // ------------------------------------------------
                // Select student:
                // 1. Login from URL params, if it exists in API list
                // 2. Otherwise first actual student
                // ------------------------------------------------

                const studentFromParams = loginParam
                    ? studentOptions.find(
                        student =>
                            student.value === loginParam
                    )
                    : undefined;

                if (studentFromParams) {
                    setSelectedStudent(studentFromParams.value);
                } else {
                    // Skip "-- Select --" and select first real student
                    const firstStudent = studentOptions.find(
                        student => student.value !== ''
                    );

                    setSelectedStudent(firstStudent?.value || '');
                }

            } catch (error: any) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        if (schoolCode && familyCode) {
            fetchStudents();
        }
    }, [schoolCode, familyCode, loginParam, calledFrom]);

    // 2. Fetch Subjects
    useEffect(() => {
        if (!selectedStudent || selectedStudent === '-Select-' || selectedStudent === '') {
            setSubjects([]); return;
        }
        const fetchSubjects = async () => {
            setLoadingSubjects(true);
            try {
                const url = `${API_BASE_URL}api/diary/subjects/?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${selectedStudent}`;
                const response = await fetch(url);
                if (response.ok) {
                    const data = await response.json();
                    const options = data.map((s: string) => ({ value: s, label: s }));
                    setSubjects(options);
                    setSelectedSubject('');
                }
            } catch (err) { console.error(err); }
            finally { setLoadingSubjects(false); }
        };
        fetchSubjects();
    }, [selectedStudent, schoolCode, familyCode]);

    // 3. Fetch Levels
    useEffect(() => {
        if (!selectedStudent) {
            setLevels([]);
            return;
        }

        const fetchLevels = async () => {
            setLoadingLevels(true);
            try {
                const subject = selectedSubject || "ALL";

                const url =
                    `${API_BASE_URL}api/diary/levels/?SchoolCode=${schoolCode}&FamilyCode=${familyCode}&Login=${selectedStudent}&Subject=${encodeURIComponent(subject)}`;

                const response = await fetch(url);

                if (response.ok) {
                    const data = await response.json();
                    setLevels(data);
                    setSelectedLevel('');
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingLevels(false);
            }
        };

        fetchLevels();
    }, [selectedSubject, selectedStudent, schoolCode, familyCode]);
    const handleSubmit = async () => {
        if (!selectedStudent || selectedStudent === '-Select-' || selectedStudent === '') {
            setAlertInfo({ show: true, message: 'Please select a student.' });
            return;
        }

        setLoading(true);
        setError(null);
        setHasSearched(true);
        setReportData([]);
        setCurrentPage(1);

        try {
            const params = new URLSearchParams({
                SchoolCode: schoolCode,
                FamilyCode: familyCode,
                student: selectedStudent,
                subject: selectedSubject || '',
                level: selectedLevel || '',
                performance: selectedPerformance,
                CalledFrom: calledFrom
            });

            const response = await fetch(`${API_BASE_URL}api/get_progress_report/?${params.toString()}`);
            if (!response.ok) throw new Error("Failed to generate report.");
            const data = await response.json();

            // Map backend fields to frontend interface
            const mappedData: ReportEntry[] = (data.data || []).map((item: any) => ({
                typeId: item.Type,
                activity: item.LessonDesc,
                lastDoneOn: item.LastDone,
                lastScore: item.Last_Score,
                averageScore: item.AVGScore,
                timesDone: item.TimesDone,
                chapter: item.ChapterDesc,
                unit: item.UnitDesc,
                level: item.LevelDesc,
                color: item.TextColor || 'black' // Use backend color
            }));

            setReportData(mappedData);

        } catch (error: any) {
            setError("Failed to fetch report.");
        } finally {
            setLoading(false);
        }
    };

    const getRowColor = (score: number) => {
        if (score >= 90) return 'green';
        if (score >= 80) return '#C8AA00'; // Dark Yellow / Gold
        return 'red';
    };

    // --- Updated Image Render Logic ---
    const getTypeContent = (typeId: number) => {
        switch (typeId) {
            case 9:
                return <img src={ATestImg} alt="Test" style={{ verticalAlign: 'middle' }} />;
            case 11:
                return <img src={BrickImg} alt="Brick" style={{ verticalAlign: 'middle' }} />;
            case 14:
                return <img src={KnowledgeImg} alt="Knowledge" style={{ verticalAlign: 'middle' }} />;
            // Standard Activities (Type 8) have no icon in legacy output
            default:
                return null;
        }
    };

    // Click a header: first click on a new column sorts descending for
    // date/number columns (newest/highest first) and ascending for text columns
    // (a column can override this with firstDirection); clicking the same
    // column again reverses it. Sorting only affects the rows on the current
    // page (same as the old VB page).
    const handleSort = (col: ColumnDef) => {
        setSortConfig(prev => {
            if (prev && prev.key === col.key) {
                return { key: col.key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            const first: SortDirection = col.firstDirection || (col.type === 'text' ? 'asc' : 'desc');
            return { key: col.key, direction: first };
        });
    };

    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const totalPages = Math.ceil(reportData.length / ITEMS_PER_PAGE);

    // Slice the page first, then sort only those rows
    const currentItems = useMemo(() => {
        const pageRows = reportData.slice(indexOfFirstItem, indexOfLastItem);
        if (!sortConfig) return pageRows;
        const col = COLUMNS.find(c => c.key === sortConfig.key);
        if (!col) return pageRows;

        const dir = sortConfig.direction === 'asc' ? 1 : -1;

        return pageRows
            .map((row, index) => ({ row, index, value: getSortValue(row, col) }))
            .sort((a, b) => {
                // Empty / unparseable values always go to the bottom
                if (a.value === null && b.value === null) return a.index - b.index;
                if (a.value === null) return 1;
                if (b.value === null) return -1;

                let cmp = 0;
                if (typeof a.value === 'number' && typeof b.value === 'number') {
                    cmp = a.value - b.value;
                } else {
                    cmp = String(a.value).localeCompare(String(b.value), undefined, { numeric: true });
                }

                if (cmp === 0) return a.index - b.index;
                return cmp * dir;
            })
            .map(item => item.row);
    }, [reportData, sortConfig, indexOfFirstItem, indexOfLastItem]);

    const changePage = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerSection}>
                <div style={styles.topBanner}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <img src={logoImg} alt="KE Logo" style={styles.logo} />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            color: "blue",
                            textDecoration: "underline",
                            cursor: "pointer",
                        }}
                        onClick={() => {
                            if (calledFrom === "SysAdmin") {
                                window.close();
                            } else if (calledFrom) {
                                window.close();
                            } else {
                                navigate("/SysAdmin");
                            }
                        }}
                    >
                        cancel
                    </div>
                </div>
                <div style={styles.pageTitle}>
                    <span style={{ ...styles.pageTitle, color: 'white' }}>Progress Report:</span>&nbsp;
                    {/* <span style={{ ...styles.pageTitle, color: '#FDCE47' }}>Family - {familyCode}</span> */}
                    <span style={{ ...styles.pageTitle, color: '#FDCE47' }}>Family - {sessionStorage.getItem('surname')}</span>
                </div>

                <div style={styles.controlsPanel}>
                    <table style={styles.controlsTable}>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.controlLabel, width: '5%' }}>Child</td>
                                <td style={{ ...styles.controlCell, width: '20%' }}>
                                    {/* <select style={styles.select} value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} disabled={loginParam !== 'A' && loginParam !== ''}>
                                        {students.map((s, idx) => <option key={idx} value={s.value}>{s.label}</option>)}
                                    </select> */}
                                    {/* REMOVED DISABLED ATTRIBUTE SO THAT USER CAN SELECT STUDENT FROM DROPDOWN - 04/09/2026*/}
                                    <select
                                        style={styles.select}
                                        value={selectedStudent}
                                        onChange={(e) => setSelectedStudent(e.target.value)}
                                    >
                                        {students.map((s, idx) => (
                                            <option key={idx} value={s.value}>
                                                {s.label}
                                            </option>
                                        ))}
                                    </select>
                                </td>

                                <td style={{ ...styles.controlLabel, width: '5%' }}>Subject</td>
                                <td style={{ ...styles.controlCell, width: '20%' }}>
                                    <select style={styles.select} value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}>
                                        <option value="">ALL</option>
                                        {subjects.map(sub => <option key={sub.value} value={sub.label}>{sub.label}</option>)}
                                    </select>
                                </td>

                                <td style={{ ...styles.controlLabel, width: '5%' }}>Level</td>
                                <td style={{ ...styles.controlCell, width: '20%' }}>
                                    <select style={styles.select} value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)}>
                                        <option value="">ALL</option>
                                        {levels.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                    </select>
                                </td>

                                <td style={{ ...styles.controlLabel, width: '8%' }}>Performance</td>
                                <td style={{ ...styles.controlCell, width: '15%' }}>
                                    <select style={styles.select} value={selectedPerformance} onChange={(e) => setSelectedPerformance(e.target.value)}>
                                        {performanceOptions.map(opt => <option key={opt.value} value={opt.label}>{opt.label}</option>)}
                                    </select>
                                </td>
                                <td></td>
                            </tr>

                            <tr><td colSpan={9} style={{ height: '8px' }}></td></tr>

                            <tr>
                                <td colSpan={8} style={{ ...styles.controlCell, textAlign: 'center' as 'center' }}>
                                    <img src={legendImg} alt="Legend" style={{ border: 'none' }} />
                                </td>
                                <td style={{ textAlign: 'right', verticalAlign: 'bottom' }}>
                                    <img
                                        src={isHovered ? submit_o : submit_n}
                                        alt="Submit"
                                        onClick={handleSubmit}
                                        onMouseEnter={() => setIsHovered(true)}
                                        onMouseLeave={() => setIsHovered(false)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {(loading || loadingSubjects || loadingLevels) && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Loading /></div>}
            {error && <div style={styles.errorBox}>{error}</div>}

            {hasSearched && (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, textAlign: 'center', whiteSpace: 'nowrap' }}>#</th>
                                    {COLUMNS.map(col => {
                                        const isActive = sortConfig?.key === col.key;
                                        return (
                                            <th
                                                key={col.key}
                                                style={{
                                                    ...styles.th,
                                                    ...styles.thSortable,
                                                    whiteSpace: 'nowrap',
                                                    ...(col.center ? { textAlign: 'center' as const } : {}),
                                                    ...(col.width ? { width: col.width } : {})
                                                }}
                                                onClick={() => handleSort(col)}
                                                title={`Click to sort by ${col.label}`}
                                            >
                                                {col.label}
                                                <span style={styles.sortArrow}>
                                                    {isActive ? (sortConfig!.direction === 'asc' ? '▲' : '▼') : ''}
                                                </span>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length === 0 ? (
                                    <tr><td colSpan={10} style={{ textAlign: 'center', padding: '20px', color: '#666' }}></td></tr>
                                ) : (
                                    currentItems.map((row, idx) => {
                                        const rowStyle = { color: row.color };

                                        return (
                                            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#F4FAFF' : 'white' }}>
                                                <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>{indexOfFirstItem + idx + 1}</td>
                                                <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                    {getTypeContent(row.typeId)}
                                                </td>
                                                <td style={{ ...styles.td, ...rowStyle, whiteSpace: 'nowrap' }}>{row.activity}</td>
                                                <td style={{ ...styles.td, ...rowStyle, whiteSpace: 'nowrap' }}>{row.lastDoneOn}</td>

                                                <td style={{ ...styles.td, textAlign: 'center', color: row.color, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                                    {row.lastScore}
                                                </td>

                                                <td style={{ ...styles.td, textAlign: 'center', ...rowStyle, whiteSpace: 'nowrap' }}>{row.averageScore}</td>
                                                <td style={{ ...styles.td, textAlign: 'center', ...rowStyle, whiteSpace: 'nowrap' }}>{row.timesDone}</td>
                                                <td style={{ ...styles.td, ...rowStyle, whiteSpace: 'nowrap' }}>{row.chapter}</td>
                                                <td style={{ ...styles.td, ...rowStyle, whiteSpace: 'nowrap' }}>{row.unit}</td>
                                                <td style={{ ...styles.td, ...rowStyle, whiteSpace: 'nowrap' }}>{row.level}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {reportData.length > 0 && (
                        <div style={styles.paginationBar}>
                            <span style={currentPage === 1 ? styles.pageLinkDisabled : styles.pageLink} onClick={() => changePage(1)}>&lt;&lt;</span>
                            <span style={currentPage === 1 ? styles.pageLinkDisabled : styles.pageLink} onClick={() => changePage(currentPage - 1)}>&lt;</span>
                            <span style={{ color: 'black', margin: '0 5px' }}>{currentPage} of {totalPages}</span>
                            <span style={currentPage === totalPages ? styles.pageLinkDisabled : styles.pageLink} onClick={() => changePage(currentPage + 1)}>&gt;</span>
                            <span style={currentPage === totalPages ? styles.pageLinkDisabled : styles.pageLink} onClick={() => changePage(totalPages)}>&gt;&gt;</span>
                        </div>
                    )}
                </>
            )}
            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                />
            )}
        </div>
    );
};

export default EL_PTReport;